import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_REGIONS,
  localPartOf,
  phoneCountryOf,
  phoneExample,
  phoneProblem,
  phoneRegionByCode,
  toE164,
  type PhoneRegion,
} from "@zx/contracts";

import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  手机号输入：**地区选择器 + 号码**
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「手机号调整为支持不同地区，可以参考隐形眼镜项目的会员基本资料里面的
 * 手机号，也是默认台湾，然后是中国，下面是其他地区手机号」。
 *
 * 🔴 **规则和地区清单都在契约的 `phone.ts`**（吃 `libphonenumber-js`，245 个地区）。
 *    这里一行判断都不自己写 —— 前端写一份、后端再写一份，两份迟早对不上，
 *    而对不上的症状是「前面填得过、送出去被打回来」，用户完全看不出哪儿错了。
 *
 * 🔴 **不用原生 `<select>`**：245 个地区没有搜索根本找不到（要滚十几屏），
 *    而 `<option>` 里也塞不进搜索框。所以是自绘的弹出列表。
 *
 * ⚠️ **不画国旗。** 参考项目用的是 `flag-icons` 的 SVG，因为
 *    **Windows 不画国旗 emoji**（系统字体故意不含那些字形），
 *    在老板的机器上 🇹🇼 会变成「TW」两个字母。与其多引一个包和一份 CSS，
 *    不如就「名称 + 国码」—— 搜索本来就比认旗子快。
 */
export function PhoneInput({
  value,
  onChange,
  showProblem = false,
  label = "手机号",
}: {
  /** 当前的 E.164（空串 = 还没填）。 */
  value: string;
  onChange: (e164: string) => void;
  /** 提交过一次之后才红字，刚打开时别吓人。 */
  showProblem?: boolean;
  label?: string;
}) {
  /**
   * 从已有的号码推回「哪个地区 + 本地号码」。
   * ⚠️ 认不出地区（没填、或者是老数据）时退回默认地区，
   *    但**号码原样留着** —— 直接清掉等于把用户填过的东西吃掉。
   */
  const initial = useMemo(() => {
    const region = value === "" ? undefined : phoneCountryOf(value);
    return {
      code: region?.code ?? DEFAULT_PHONE_COUNTRY,
      local: value === "" ? "" : localPartOf(value),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [code, setCode] = useState(initial.code);
  const [local, setLocal] = useState(initial.local);
  const [open, setOpen] = useState(false);

  const region = phoneRegionByCode(code);
  const e164 = local.trim() === "" ? "" : toE164(code, local);
  const problem = e164 === "" ? "请输入手机号" : phoneProblem(e164);
  const example = phoneExample(code);

  const push = (nextCode: string, nextLocal: string) => {
    setCode(nextCode);
    setLocal(nextLocal);
    onChange(nextLocal.trim() === "" ? "" : toE164(nextCode, nextLocal));
  };

  return (
    <div>
      <span className="mb-1.5 block text-[13px] text-muted-foreground">{label}</span>
      <div className="flex items-stretch gap-2">
        <RegionPicker
          open={open}
          onOpenChange={setOpen}
          selected={region}
          onSelect={(next) => {
            push(next.code, local);
            setOpen(false);
          }}
        />

        <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-border bg-background px-3 focus-within:border-brand-500">
          {/*
            国码**印出来**：用户才知道自己填的号码前面会被接上什么。
            ⚠️ 用正常的前景色而不是灰色：它是**已经确定的值**，
               跟同样灰色的 placeholder 摆在一起会被读成同一串提示文字。
          */}
          <span className="shrink-0 text-[16px]">+{region?.dial ?? ""}</span>
          <input
            type="tel"
            inputMode="tel"
            value={local}
            onChange={(e) => push(code, e.target.value)}
            placeholder={example}
            maxLength={20}
            aria-label={label}
            /** ⚠️ 16px 起步：iOS Safari 对更小的输入框会自动放大整页且不缩回去。 */
            className="h-11 w-full min-w-0 border-0 bg-transparent px-0 text-[16px] outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/*
        🔴 **只在出错时说话，平时不挂提示。**（老板：「去掉下面的提示文字」）
           号码长什么样已经在输入框的 placeholder 里了，下面再挂一行灰字
           说同一件事，就是每个用户都要读一遍的噪音。

        ⚠️ 出错时那句**要讲清错在哪**（「这不像中国的手机号（该地区的格式像这样：
           131 2345 6789）」），不要只说「格式不正确」—— 只说格式不对的话，
           填了座机的人会一直重打同一个号。文案由契约的 `phoneProblem` 产生，
           跟后端拦下来时说的是同一句话。
      */}
      {showProblem && problem !== null && (
        <p className="mt-1 text-[12px] text-destructive">{problem}</p>
      )}
    </div>
  );
}

/**
 * 地区选择器：**带搜索的弹出列表**。
 *
 * ⚠️ 搜索要同时比对**中文名、ISO 代码、国码**：用户可能打「日本」「JP」「81」，
 *    只比名称的话后两种查不到，而两百多条列表用滚的找不着。
 */
function RegionPicker({
  open,
  onOpenChange,
  selected,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selected: PhoneRegion | undefined;
  onSelect: (region: PhoneRegion) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  /** 点外面和按 Esc 都要关。⚠️ 用 `pointerdown` 不是 `click`：列表滚动时别误关。 */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (boxRef.current?.contains(event.target as Node) === false) onOpenChange(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  /** ⚠️ 每次打开都从干净的列表开始，别留着上一次的关键词。 */
  useEffect(() => {
    if (open) setKeyword("");
  }, [open]);

  const rows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (kw === "") return PHONE_REGIONS;
    return PHONE_REGIONS.filter(
      (r) =>
        r.name.toLowerCase().includes(kw) ||
        r.code.toLowerCase().includes(kw) ||
        r.dial.includes(kw.replace(/^\+/, "")),
    );
  }, [keyword]);

  return (
    <div ref={boxRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="flex h-11 w-[7.5rem] items-center gap-1 rounded-lg border border-border bg-background px-3 text-left text-[15px] transition-colors hover:border-brand-500"
      >
        <span className="min-w-0 flex-1 truncate">{selected?.name ?? "选择地区"}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        /*
         * 🔴 **`absolute` 不是 `fixed`**：这一格常常出现在会滚的区块里，
         *    `fixed` 的话滚动时列表会留在原地，跟按钮分家。
         */
        <div className="absolute top-full left-0 z-30 mt-1 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜国家 / 地区或国码"
              aria-label="搜国家或地区"
              className="h-9 w-full min-w-0 border-0 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground"
            />
            {keyword !== "" && (
              <button
                type="button"
                onClick={() => setKeyword("")}
                aria-label="清除搜索"
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          {/* max-h：手机上也不会盖掉半个屏幕；列表自己滚。 */}
          <ul role="listbox" className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {rows.length === 0 ? (
              <li className="px-3 py-6 text-center text-[13px] text-muted-foreground">
                找不到符合的地区
              </li>
            ) : (
              rows.map((r) => {
                const active = r.code === selected?.code;
                return (
                  <li key={r.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => onSelect(r)}
                      className={cn(
                        "flex min-h-11 w-full items-center gap-2.5 px-3 text-left text-[14px] transition-colors hover:bg-secondary",
                        active ? "text-brand-700" : "text-foreground",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{r.name}</span>
                      <span className="shrink-0 text-[12px] text-muted-foreground">+{r.dial}</span>
                      {active && <Check className="size-4 shrink-0 text-brand-600" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
